import { clone, createId } from "../scene/ops";
import {
  type AudioClip,
  MAX_TRACKS,
  type OverlayBox,
  type OverlayClip,
  type SceneDocument,
  type SceneElement,
  type Timeline,
  type TimeRange,
  type VideoClip,
} from "../scene/schema";
import { compactTracks, isTrackFree, trackEntries } from "./tracks";

/**
 * Timeline of video documents (docs/05-video-und-untertitel.md). The main
 * track is an ordered list of clips that play back to back, so a clip's start
 * follows from the clips before it: deleting closes the gap (ripple) and
 * reordering is just moving an entry. A transition lets a clip start before
 * the previous one ends (they overlap by its duration). Clips on the video
 * tracks above (overlays) are freely placed like music. Everything here is
 * pure or mutates the given document in place (plain objects and Immer drafts).
 */

/** Shortest clip the editor allows. */
export const MIN_CLIP_MS = 100;
/** Default display time of a still image on the main track. */
export const IMAGE_CLIP_MS = 4000;
/** Length of a video without clips (text-only animations). */
export const EMPTY_VIDEO_MS = 5000;

/** Shortest transition the editor offers. */
export const MIN_TRANSITION_MS = 100;

export const EMPTY_TIMELINE: Timeline = {
  fps: 30,
  clips: [],
  audio: [],
  captions: { overrides: {} },
};

export interface ClipLayout {
  clip: VideoClip;
  index: number;
  /** Timeline position (ms). */
  start: number;
  end: number;
  /** Overlap with the previous clip (its transition), 0 for a hard cut. */
  overlap: number;
}

export function clipDuration(clip: Pick<VideoClip, "sourceIn" | "sourceOut" | "speed">): number {
  return Math.max(1, (clip.sourceOut - clip.sourceIn) / (clip.speed || 1));
}

export function audioClipEnd(clip: AudioClip): number {
  return clip.start + (clip.sourceOut - clip.sourceIn);
}

/**
 * Overlap of a transition between two clips: at most half of either clip, so
 * no more than two clips are ever visible at once.
 */
export function transitionOverlap(previous: VideoClip, clip: VideoClip): number {
  if (!clip.transition) return 0;
  const limit = Math.min(clipDuration(previous), clipDuration(clip)) / 2;
  return Math.max(0, Math.min(clip.transition.duration, limit));
}

/** Start and end of every main track clip. */
export function layoutClips(timeline: Pick<Timeline, "clips"> | undefined): ClipLayout[] {
  const out: ClipLayout[] = [];
  let cursor = 0;
  let previous: VideoClip | null = null;
  for (const [index, clip] of (timeline?.clips ?? []).entries()) {
    const duration = clipDuration(clip);
    const overlap = previous ? transitionOverlap(previous, clip) : 0;
    const start = cursor - overlap;
    out.push({ clip, index, start, end: start + duration, overlap });
    cursor = start + duration;
    previous = clip;
  }
  return out;
}

export function mainTrackDuration(timeline: Pick<Timeline, "clips"> | undefined): number {
  const layout = layoutClips(timeline);
  return layout[layout.length - 1]?.end ?? 0;
}

export function overlayClipEnd(
  clip: Pick<OverlayClip, "start" | "sourceIn" | "sourceOut" | "speed">,
): number {
  return clip.start + clipDuration(clip);
}

/** Overlay clips of a timeline (documents from before the overlay tracks have none). */
export function overlayClips(timeline: Pick<Timeline, "overlays"> | undefined): OverlayClip[] {
  return timeline?.overlays ?? [];
}

/**
 * Length of the video: the main track and the video tracks above it, or –
 * without clips – the latest element (text-only animations). Music never
 * makes a video longer.
 */
export function videoDuration(doc: Pick<SceneDocument, "timeline" | "elements">): number {
  let videos = mainTrackDuration(doc.timeline);
  for (const clip of overlayClips(doc.timeline)) videos = Math.max(videos, overlayClipEnd(clip));
  if (videos > 0) return Math.round(videos);
  let latest = 0;
  for (const element of Object.values(doc.elements)) {
    if (element.time) latest = Math.max(latest, element.time.end);
  }
  return latest > 0 ? latest : EMPTY_VIDEO_MS;
}

/**
 * Clip under the timeline position `t` (end exclusive; the last clip also owns
 * its end). During a transition this is the incoming clip.
 */
export function clipAt(layout: readonly ClipLayout[], t: number): ClipLayout | null {
  if (layout.length === 0) return null;
  // Starts (and ends) grow monotonically: find the last clip that started.
  let low = 0;
  let high = layout.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (layout[mid]!.start <= t) {
      found = mid;
      low = mid + 1;
    } else high = mid - 1;
  }
  if (found < 0) return null;
  const entry = layout[found]!;
  if (t < entry.end) return entry;
  return found === layout.length - 1 && Math.abs(t - entry.end) < 1 ? entry : null;
}

export interface TransitionState {
  /** Outgoing and incoming clip. */
  from: ClipLayout;
  to: ClipLayout;
  /** 0 at the start of the transition, 1 at its end. */
  progress: number;
}

/** The transition running at `t`, if any. */
export function transitionAt(layout: readonly ClipLayout[], t: number): TransitionState | null {
  const to = clipAt(layout, t);
  if (!to || to.overlap <= 0 || to.index === 0) return null;
  const local = t - to.start;
  if (local >= to.overlap) return null;
  return { from: layout[to.index - 1]!, to, progress: Math.max(0, local / to.overlap) };
}

/** Source position (ms) shown at timeline position `t` of a clip. */
export function sourceTimeAt(entry: ClipLayout, t: number): number {
  const local = Math.max(0, Math.min(t, entry.end) - entry.start);
  return Math.min(entry.clip.sourceOut, entry.clip.sourceIn + local * entry.clip.speed);
}

/** Timeline position of a source position inside a clip. */
export function timelineTimeAt(entry: ClipLayout, sourceMs: number): number {
  return entry.start + (sourceMs - entry.clip.sourceIn) / entry.clip.speed;
}

/** Frame-exact time: rounded to the frame grid of the timeline. */
export function snapToFrame(t: number, fps: number): number {
  const frame = 1000 / fps;
  return Math.round(Math.round(t / frame) * frame);
}

export function formatTimecode(ms: number, withFraction = true): string {
  const clamped = Math.max(0, ms);
  const minutes = Math.floor(clamped / 60_000);
  const seconds = Math.floor((clamped % 60_000) / 1000);
  const tenths = Math.floor((clamped % 1000) / 100);
  const base = `${minutes}:${String(seconds).padStart(2, "0")}`;
  return withFraction ? `${base},${tenths}` : base;
}

// ---------------------------------------------------------------------------
// Operations (mutating)
// ---------------------------------------------------------------------------

export function ensureTimeline(doc: SceneDocument): Timeline {
  if (!doc.timeline) doc.timeline = clone(EMPTY_TIMELINE);
  return doc.timeline;
}

export function createVideoClip(
  asset: { id: string; kind: string; durationMs: number | null; name?: string },
  input: Partial<VideoClip> = {},
): VideoClip {
  const image = asset.kind === "image" || asset.kind === "svg";
  const sourceOut = image
    ? IMAGE_CLIP_MS
    : Math.max(MIN_CLIP_MS, asset.durationMs ?? IMAGE_CLIP_MS);
  return {
    id: createId("clip"),
    kind: image ? "image" : "video",
    assetId: asset.id,
    ...(asset.name ? { name: asset.name } : {}),
    sourceIn: 0,
    sourceOut,
    speed: 1,
    volume: 1,
    muted: false,
    fadeIn: 0,
    fadeOut: 0,
    framing: { fit: "cover", zoom: 1, x: 0, y: 0, flipX: false },
    background: "color",
    ...input,
  };
}

export function createAudioClip(
  asset: { id: string; durationMs: number | null; name?: string },
  input: Partial<AudioClip> = {},
): AudioClip {
  return {
    id: createId("audio"),
    assetId: asset.id,
    ...(asset.name ? { name: asset.name } : {}),
    start: 0,
    sourceIn: 0,
    sourceOut: Math.max(MIN_CLIP_MS, asset.durationMs ?? 30_000),
    volume: 0.5,
    fadeIn: 0,
    fadeOut: 1500,
    ducking: true,
    ...input,
  };
}

/** Inserts a clip into the main track (default: at the end). */
export function insertClip(doc: SceneDocument, clip: VideoClip, index?: number): void {
  const timeline = ensureTimeline(doc);
  const at =
    index === undefined
      ? timeline.clips.length
      : Math.max(0, Math.min(index, timeline.clips.length));
  // Inserting before existing clips pushes the content after it; appending changes nothing.
  const position = at < timeline.clips.length ? layoutClips(timeline)[at]!.start : null;
  const before = mainTrackDuration(timeline);
  timeline.clips.splice(at, 0, clip);
  if (position !== null) rippleAfter(doc, position, mainTrackDuration(timeline) - before);
}

/** Index where a clip dropped at timeline position `t` goes (before/after the clip under it). */
export function insertionIndexAt(timeline: Timeline | undefined, t: number): number {
  const layout = layoutClips(timeline);
  for (const entry of layout) {
    if (t < (entry.start + entry.end) / 2) return entry.index;
  }
  return layout.length;
}

/**
 * Splits the main track clip under `t`. Returns the id of the new right part,
 * or null when `t` is too close to a cut.
 */
export function splitClipAt(doc: SceneDocument, t: number): string | null {
  const timeline = doc.timeline;
  if (!timeline) return null;
  const layout = layoutClips(timeline);
  const entry = clipAt(layout, t);
  if (!entry) return null;
  if (t - entry.start < MIN_CLIP_MS / 2 || entry.end - t < MIN_CLIP_MS / 2) return null;
  // Not inside a transition: the cut would have to split two clips at once.
  const next = layout[entry.index + 1];
  if (t < entry.start + entry.overlap || (next && t >= next.start)) return null;
  const cut = sourceTimeAt(entry, t);
  const left = timeline.clips[entry.index]!;
  const right: VideoClip = { ...clone(left), id: createId("clip"), sourceIn: cut, fadeIn: 0 };
  delete right.transition;
  left.sourceOut = cut;
  left.fadeOut = 0;
  timeline.clips.splice(entry.index + 1, 0, right);
  return right.id;
}

/** Splits the audio clip `clipId` at timeline position `t`. */
export function splitAudioAt(doc: SceneDocument, clipId: string, t: number): string | null {
  const timeline = doc.timeline;
  const index = timeline?.audio.findIndex((clip) => clip.id === clipId) ?? -1;
  if (!timeline || index < 0) return null;
  const clip = timeline.audio[index]!;
  const local = t - clip.start;
  if (local < MIN_CLIP_MS / 2 || clip.sourceOut - clip.sourceIn - local < MIN_CLIP_MS / 2)
    return null;
  const cut = clip.sourceIn + local;
  const right: AudioClip = {
    ...clone(clip),
    id: createId("audio"),
    start: t,
    sourceIn: cut,
    fadeIn: 0,
  };
  clip.sourceOut = cut;
  clip.fadeOut = 0;
  timeline.audio.splice(index + 1, 0, right);
  return right.id;
}

/**
 * Moves a clip edge to a new source position (ripple: later clips, overlays
 * and music move along). `maxSource` is the length of the source file.
 */
export function trimClip(
  doc: SceneDocument,
  clipId: string,
  edge: "in" | "out",
  sourceMs: number,
  maxSource = Number.POSITIVE_INFINITY,
): void {
  const timeline = doc.timeline;
  if (!timeline) return;
  const layout = layoutClips(timeline);
  const entry = layout.find((item) => item.clip.id === clipId);
  if (!entry) return;
  const clip = timeline.clips[entry.index]!;
  const before = mainTrackDuration(timeline);
  const minLength = MIN_CLIP_MS * clip.speed;
  if (edge === "in") {
    clip.sourceIn = Math.max(0, Math.min(sourceMs, clip.sourceOut - minLength));
  } else {
    const limit = clip.kind === "image" ? 10 * 60_000 : maxSource;
    clip.sourceOut = Math.min(limit, Math.max(sourceMs, clip.sourceIn + minLength));
  }
  const delta = mainTrackDuration(timeline) - before;
  if (Math.abs(delta) > 0.5) rippleAfter(doc, rippleAnchor(layout, entry), delta);
}

/** Content from here on moves with the clips after `entry`. */
function rippleAnchor(layout: readonly ClipLayout[], entry: ClipLayout): number {
  return layout[entry.index + 1]?.start ?? entry.end;
}

/** Removes a main track clip; everything after it moves up (ripple delete). */
export function removeClip(doc: SceneDocument, clipId: string): void {
  const timeline = doc.timeline;
  if (!timeline) return;
  const layout = layoutClips(timeline);
  const entry = layout.find((item) => item.clip.id === clipId);
  if (!entry) return;
  const before = mainTrackDuration(timeline);
  timeline.clips.splice(entry.index, 1);
  // The first clip never has a transition.
  if (entry.index === 0 && timeline.clips[0]) delete timeline.clips[0].transition;
  rippleAfter(doc, rippleAnchor(layout, entry), mainTrackDuration(timeline) - before);
}

export function removeAudioClip(doc: SceneDocument, clipId: string): void {
  const timeline = doc.timeline;
  if (!timeline) return;
  timeline.audio = timeline.audio.filter((clip) => clip.id !== clipId);
}

export function moveClip(doc: SceneDocument, clipId: string, toIndex: number): void {
  const clips = doc.timeline?.clips;
  if (!clips) return;
  const from = clips.findIndex((clip) => clip.id === clipId);
  if (from < 0) return;
  const [clip] = clips.splice(from, 1);
  const target = Math.max(0, Math.min(toIndex > from ? toIndex - 1 : toIndex, clips.length));
  clips.splice(target, 0, clip!);
  if (clips[0]) delete clips[0].transition;
}

/** Sets (or with null removes) the transition into the main track clip `clipId`. */
export function setTransition(
  doc: SceneDocument,
  clipId: string,
  transition: VideoClip["transition"] | null,
): void {
  const timeline = doc.timeline;
  if (!timeline) return;
  const layout = layoutClips(timeline);
  const entry = layout.find((item) => item.clip.id === clipId);
  if (!entry || entry.index === 0) return;
  const before = mainTrackDuration(timeline);
  const clip = timeline.clips[entry.index]!;
  if (transition) clip.transition = { ...transition };
  else delete clip.transition;
  // The clips after the cut move by the change of the overlap, and their overlays with them.
  rippleAfter(doc, entry.start, mainTrackDuration(timeline) - before);
}

export function duplicateClip(doc: SceneDocument, clipId: string): string | null {
  const clips = doc.timeline?.clips;
  const index = clips?.findIndex((clip) => clip.id === clipId) ?? -1;
  if (!clips || index < 0) return null;
  const copy = { ...clone(clips[index]!), id: createId("clip") };
  delete copy.transition;
  insertClip(doc, copy, index + 1);
  return copy.id;
}

/**
 * Shifts overlays (elements with a time range) and music that start at or
 * after `at` by `delta` ms. Items spanning `at` grow/shrink at their end.
 */
export function rippleAfter(doc: SceneDocument, at: number, delta: number): void {
  if (Math.abs(delta) < 0.5) return;
  const shiftRange = (range: TimeRange): TimeRange | null => {
    if (range.start >= at - 0.5) {
      const start = Math.max(0, range.start + delta);
      const end = Math.max(start + MIN_CLIP_MS, range.end + delta);
      return { start: Math.round(start), end: Math.round(end) };
    }
    if (range.end > at) {
      const end = Math.max(range.start + MIN_CLIP_MS, range.end + delta);
      return { start: range.start, end: Math.round(end) };
    }
    return null;
  };
  for (const element of Object.values(doc.elements)) {
    if (!element.time) continue;
    const next = shiftRange(element.time);
    if (next) element.time = next;
  }
  for (const clip of [...(doc.timeline?.audio ?? []), ...overlayClips(doc.timeline)]) {
    if (clip.start >= at - 0.5) clip.start = Math.max(0, Math.round(clip.start + delta));
  }
}

/**
 * Cuts timeline ranges out of the main track ("Stille entfernen", "Schneiden
 * per Text"). Overlays and music after a cut move up.
 */
export function removeTimeRanges(doc: SceneDocument, ranges: readonly TimeRange[]): number {
  const timeline = doc.timeline;
  if (!timeline) return 0;
  // Merge and process from the end, so earlier positions stay valid.
  const sorted = [...ranges]
    .filter((range) => range.end - range.start >= 1)
    .sort((a, b) => a.start - b.start);
  const merged: TimeRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) last.end = Math.max(last.end, range.end);
    else merged.push({ ...range });
  }
  let removed = 0;
  for (const range of merged.reverse()) {
    const layout = layoutClips(timeline);
    const replacement: VideoClip[] = [];
    for (const entry of layout) {
      const { clip } = entry;
      if (range.end <= entry.start || range.start >= entry.end) {
        replacement.push(clip);
        continue;
      }
      const cutFrom = sourceTimeAt(entry, Math.max(range.start, entry.start));
      const cutTo = sourceTimeAt(entry, Math.min(range.end, entry.end));
      if (cutFrom - clip.sourceIn >= MIN_CLIP_MS * clip.speed) {
        replacement.push({ ...clip, sourceOut: cutFrom, fadeOut: 0 });
      }
      if (clip.sourceOut - cutTo >= MIN_CLIP_MS * clip.speed) {
        const keepId = cutFrom - clip.sourceIn < MIN_CLIP_MS * clip.speed;
        const rest: VideoClip = {
          ...clip,
          id: keepId ? clip.id : createId("clip"),
          sourceIn: cutTo,
          fadeIn: 0,
        };
        // Only the part that still starts where the clip started keeps its transition.
        if (!keepId) delete rest.transition;
        replacement.push(rest);
      }
    }
    const before = mainTrackDuration(timeline);
    timeline.clips = replacement;
    if (timeline.clips[0]) delete timeline.clips[0].transition;
    const delta = mainTrackDuration(timeline) - before;
    removed -= delta;
    shiftForCut(doc, range, -delta);
  }
  return removed;
}

/** Overlays and music around a removed range [start, end) that shortened the video by `length`. */
function shiftForCut(doc: SceneDocument, range: TimeRange, length: number): void {
  const map = (t: number) =>
    t <= range.start ? t : t >= range.end ? t - length : Math.max(range.start, t - length);
  for (const element of Object.values(doc.elements)) {
    if (!element.time) continue;
    const start = map(element.time.start);
    const end = Math.max(start + MIN_CLIP_MS, map(element.time.end));
    element.time = { start: Math.round(start), end: Math.round(end) };
  }
  for (const clip of [...(doc.timeline?.audio ?? []), ...overlayClips(doc.timeline)]) {
    if (clip.start >= range.end) clip.start = Math.max(0, Math.round(clip.start - length));
  }
}

// ---------------------------------------------------------------------------
// Video tracks above the main track (overlay clips)
// ---------------------------------------------------------------------------

/** Box covering the whole frame. */
export function fullFrameBox(frame: { width: number; height: number }): OverlayBox {
  return { x: 0, y: 0, width: frame.width, height: frame.height };
}

export function createOverlayClip(
  asset: { id: string; kind: string; durationMs: number | null; name?: string },
  frame: { width: number; height: number },
  input: Partial<OverlayClip> = {},
): OverlayClip {
  const main = createVideoClip(asset);
  return {
    id: createId("overlay"),
    kind: main.kind,
    assetId: asset.id,
    ...(asset.name ? { name: asset.name } : {}),
    track: 1,
    start: 0,
    sourceIn: 0,
    sourceOut: main.sourceOut,
    speed: 1,
    volume: 1,
    muted: false,
    fadeIn: 0,
    fadeOut: 0,
    box: fullFrameBox(frame),
    framing: { fit: "cover", zoom: 1, x: 0, y: 0, flipX: false },
    opacity: 1,
    radius: 0,
    ...input,
  };
}

/** Number of video tracks above the main track that hold clips. */
export function overlayTrackCount(timeline: Pick<Timeline, "overlays"> | undefined): number {
  let count = 0;
  for (const clip of overlayClips(timeline)) count = Math.max(count, clip.track);
  return count;
}

/** Whether [start, end) is free on `track` (ignoring the clip `exclude`); elements count too. */
export function trackIsFree(
  doc: SceneDocument,
  track: number,
  start: number,
  end: number,
  exclude?: string,
): boolean {
  return isTrackFree(doc, track, start, end, new Set(exclude ? [exclude] : []));
}

/** The lowest track from `track` upwards where [start, end) is free. */
export function freeTrack(
  doc: SceneDocument,
  start: number,
  end: number,
  track = 1,
  exclude?: string,
): number {
  const entries = trackEntries(doc);
  const skip = new Set(exclude ? [exclude] : []);
  let candidate = Math.max(1, track);
  while (candidate < MAX_TRACKS && !isTrackFree(doc, candidate, start, end, skip, entries)) {
    candidate++;
  }
  return candidate;
}

/**
 * Tracks without content disappear (unless kept by hand), the order stays;
 * clips and elements are renumbered (video/tracks.ts).
 */
export function compactOverlayTracks(doc: SceneDocument): void {
  if (doc.timeline) compactTracks(doc);
  overlayClips(doc.timeline).sort((a, b) => a.track - b.track || a.start - b.start);
}

/** Adds an overlay clip on its track, or on the next free track above it. */
export function addOverlayClip(doc: SceneDocument, clip: OverlayClip): void {
  const timeline = ensureTimeline(doc);
  timeline.overlays ??= [];
  clip.track = freeTrack(doc, clip.start, overlayClipEnd(clip), clip.track);
  timeline.overlays.push(clip);
  compactOverlayTracks(doc);
}

/**
 * Moves an overlay clip to another time and track. When the place is taken,
 * the clip goes to the next free track above; track `overlayTrackCount + 1`
 * creates a new track on top.
 */
export function moveOverlayClip(
  doc: SceneDocument,
  clipId: string,
  start: number,
  track: number,
): void {
  const clip = overlayClips(doc.timeline).find((item) => item.id === clipId);
  if (!clip) return;
  const length = clipDuration(clip);
  const at = Math.max(0, Math.round(start));
  clip.start = at;
  clip.track = freeTrack(doc, at, at + length, track, clip.id);
  compactOverlayTracks(doc);
}

export function removeOverlayClip(doc: SceneDocument, clipId: string): void {
  const timeline = doc.timeline;
  if (!timeline?.overlays) return;
  timeline.overlays = timeline.overlays.filter((clip) => clip.id !== clipId);
  compactOverlayTracks(doc);
}

/**
 * Moves a main track clip onto a video track above (B-roll): it starts at
 * `start` on `track` and covers the frame; the main track closes the gap.
 * `start` is a position of the timeline before the move – when it lies after
 * the clip, it moves up with the rest. Returns the new overlay clip's id.
 */
export function moveClipToTrack(
  doc: SceneDocument,
  clipId: string,
  start: number,
  track: number,
): string | null {
  const timeline = doc.timeline;
  const entry = layoutClips(timeline).find((item) => item.clip.id === clipId);
  if (!timeline || !entry) return null;
  const { clip } = entry;
  const before = mainTrackDuration(timeline);
  removeClip(doc, clipId);
  const removed = before - mainTrackDuration(timeline);
  const at = start >= entry.end - 0.5 ? start - removed : start;
  const overlay: OverlayClip = {
    id: createId("overlay"),
    kind: clip.kind,
    assetId: clip.assetId,
    ...(clip.name ? { name: clip.name } : {}),
    track,
    start: Math.max(0, Math.round(at)),
    sourceIn: clip.sourceIn,
    sourceOut: clip.sourceOut,
    speed: clip.speed,
    volume: clip.volume,
    muted: clip.muted,
    fadeIn: clip.fadeIn,
    fadeOut: clip.fadeOut,
    box: clip.box ? { ...clip.box } : fullFrameBox(doc.format),
    framing: clone(clip.framing),
    opacity: 1,
    radius: clip.radius ?? 0,
    ...(clip.color ? { color: clone(clip.color) } : {}),
  };
  addOverlayClip(doc, overlay);
  return overlay.id;
}

/** Moves a clip of an upper track into the main track at `index` (the reverse of `moveClipToTrack`). */
export function moveOverlayToMain(
  doc: SceneDocument,
  overlayId: string,
  index: number,
): string | null {
  const overlay = overlayClips(doc.timeline).find((item) => item.id === overlayId);
  if (!overlay) return null;
  removeOverlayClip(doc, overlayId);
  const clip: VideoClip = {
    id: createId("clip"),
    kind: overlay.kind,
    assetId: overlay.assetId,
    ...(overlay.name ? { name: overlay.name } : {}),
    sourceIn: overlay.sourceIn,
    sourceOut: overlay.sourceOut,
    speed: overlay.speed,
    volume: overlay.volume,
    muted: overlay.muted,
    fadeIn: overlay.fadeIn,
    fadeOut: overlay.fadeOut,
    framing: clone(overlay.framing),
    background: "color",
    ...(overlay.color ? { color: clone(overlay.color) } : {}),
  };
  insertClip(doc, clip, index);
  const first = doc.timeline?.clips[0];
  if (first) delete first.transition;
  return clip.id;
}

/** Source position (ms) an overlay clip shows at timeline position `t`. */
export function overlaySourceTimeAt(clip: OverlayClip, t: number): number {
  const local = Math.max(0, Math.min(t, overlayClipEnd(clip)) - clip.start);
  return Math.min(clip.sourceOut, clip.sourceIn + local * clip.speed);
}

/** Visible overlay clips at `t`, bottom track first. */
export function overlayClipsAt(
  timeline: Pick<Timeline, "overlays"> | undefined,
  t: number,
): OverlayClip[] {
  return overlayClips(timeline)
    .filter((clip) => t >= clip.start && t < overlayClipEnd(clip))
    .sort((a, b) => a.track - b.track);
}

/** Splits the overlay clip `clipId` at timeline position `t`. */
export function splitOverlayAt(doc: SceneDocument, clipId: string, t: number): string | null {
  const timeline = doc.timeline;
  const clip = overlayClips(timeline).find((item) => item.id === clipId);
  if (!timeline?.overlays || !clip) return null;
  const end = overlayClipEnd(clip);
  if (t - clip.start < MIN_CLIP_MS / 2 || end - t < MIN_CLIP_MS / 2) return null;
  const cut = overlaySourceTimeAt(clip, t);
  const right: OverlayClip = {
    ...clone(clip),
    id: createId("overlay"),
    start: Math.round(t),
    sourceIn: cut,
    fadeIn: 0,
  };
  clip.sourceOut = cut;
  clip.fadeOut = 0;
  timeline.overlays.push(right);
  compactOverlayTracks(doc);
  return right.id;
}

/** A copy right after the clip (same track when there is room, else above). */
export function duplicateOverlayClip(doc: SceneDocument, clipId: string): string | null {
  const clip = overlayClips(doc.timeline).find((item) => item.id === clipId);
  if (!clip) return null;
  const copy: OverlayClip = {
    ...clone(clip),
    id: createId("overlay"),
    start: Math.round(overlayClipEnd(clip)),
  };
  addOverlayClip(doc, copy);
  return copy.id;
}

/**
 * Moves an edge of an overlay clip to timeline position `t`. The clip never
 * grows into its neighbours on the same track or past its source.
 */
export function trimOverlayClip(
  doc: SceneDocument,
  clipId: string,
  edge: "in" | "out",
  t: number,
  maxSource = Number.POSITIVE_INFINITY,
): void {
  const clips = overlayClips(doc.timeline);
  const clip = clips.find((item) => item.id === clipId);
  if (!clip) return;
  // Clips and elements on the same track.
  const neighbours = trackEntries(doc).filter(
    (item) => item.track === clip.track && item.id !== clip.id,
  );
  const minLength = MIN_CLIP_MS * clip.speed;
  if (edge === "in") {
    const floor = Math.max(
      0,
      ...neighbours.map((item) => item.end).filter((end) => end <= clip.start + 0.5),
    );
    const earliest = Math.max(floor, clip.start - clip.sourceIn / clip.speed);
    const latest = overlayClipEnd(clip) - MIN_CLIP_MS;
    const start = Math.max(earliest, Math.min(latest, t));
    clip.sourceIn = Math.max(0, clip.sourceIn + (start - clip.start) * clip.speed);
    clip.start = Math.round(start);
  } else {
    const end = overlayClipEnd(clip);
    const ceiling = Math.min(
      Number.POSITIVE_INFINITY,
      ...neighbours.map((item) => item.start).filter((start) => start >= end - 0.5),
    );
    const limit = clip.kind === "image" ? 10 * 60_000 : maxSource;
    const wanted = Math.min(t, ceiling);
    clip.sourceOut = Math.min(
      limit,
      Math.max(clip.sourceIn + minLength, clip.sourceIn + (wanted - clip.start) * clip.speed),
    );
  }
}

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------

/** Visible time of an element; without `time` it spans the whole video. */
export function elementTimeRange(element: Pick<SceneElement, "time">, duration: number): TimeRange {
  return element.time ?? { start: 0, end: duration };
}

export function isVisibleAt(element: Pick<SceneElement, "time">, t: number): boolean {
  return !element.time || (t >= element.time.start && t < element.time.end);
}

function descendantIds(doc: SceneDocument, id: string, out: string[] = [], depth = 0): string[] {
  const element = doc.elements[id];
  if (element?.type !== "group" || depth > 50) return out;
  for (const child of element.children) {
    out.push(child);
    descendantIds(doc, child, out, depth + 1);
  }
  return out;
}

/**
 * Sets the visible time of an element (undefined: the whole video). A group
 * takes its content along: moved as a whole, everything in it moves by the
 * same amount; with a new start or end, elements that began or ended with the
 * group follow that edge and the rest stays inside the group's time. `base`
 * is the document before a drag, so repeated updates never add up. `mode`
 * says what the user does; without it an unchanged length counts as a move.
 */
export function setElementTime(
  doc: SceneDocument,
  id: string,
  range: TimeRange | undefined,
  base: SceneDocument = doc,
  mode?: "move" | "resize",
): void {
  const element = doc.elements[id];
  if (!element) return;
  const before = base.elements[id]?.time;
  if (range) element.time = { start: Math.round(range.start), end: Math.round(range.end) };
  else delete element.time;
  if (element.type !== "group" || !before || !range) return;
  const moved =
    mode === "move" ||
    (mode === undefined && Math.abs(range.end - range.start - (before.end - before.start)) < 1);
  for (const childId of descendantIds(doc, id)) {
    const child = doc.elements[childId];
    const original = base.elements[childId]?.time ?? child?.time;
    if (!child || !original) continue;
    if (moved) {
      const delta = range.start - before.start;
      const start = Math.max(0, original.start + delta);
      child.time = {
        start: Math.round(start),
        end: Math.round(Math.max(start + MIN_CLIP_MS, original.end + delta)),
      };
      continue;
    }
    let start = Math.abs(original.start - before.start) < 1 ? range.start : original.start;
    let end = Math.abs(original.end - before.end) < 1 ? range.end : original.end;
    start = Math.max(range.start, Math.min(start, range.end - MIN_CLIP_MS));
    end = Math.min(range.end, Math.max(end, start + MIN_CLIP_MS));
    child.time = { start: Math.round(start), end: Math.round(end) };
  }
}

/** Candidate positions for snapping on the timeline. */
export function timelineSnapPoints(
  doc: SceneDocument,
  extra: readonly number[] = [],
  exclude?: string,
): number[] {
  const points = new Set<number>([0, ...extra]);
  for (const entry of layoutClips(doc.timeline)) {
    if (entry.clip.id === exclude) continue;
    points.add(Math.round(entry.start));
    points.add(Math.round(entry.end));
  }
  for (const clip of doc.timeline?.audio ?? []) {
    if (clip.id === exclude) continue;
    points.add(clip.start);
    points.add(Math.round(audioClipEnd(clip)));
  }
  for (const clip of overlayClips(doc.timeline)) {
    if (clip.id === exclude) continue;
    points.add(clip.start);
    points.add(Math.round(overlayClipEnd(clip)));
  }
  for (const element of Object.values(doc.elements)) {
    if (!element.time || element.id === exclude) continue;
    points.add(element.time.start);
    points.add(element.time.end);
  }
  points.add(videoDuration(doc));
  return [...points].sort((a, b) => a - b);
}

/** Nearest snap point within `threshold`, or the value itself. */
export function snapTime(value: number, points: readonly number[], threshold: number): number {
  let best = value;
  let distance = threshold;
  for (const point of points) {
    const d = Math.abs(point - value);
    if (d <= distance) {
      distance = d;
      best = point;
    }
  }
  return best;
}
